import { memo, useContext, useEffect, useMemo, type ReactNode } from 'react'
import { useSelector } from 'react-redux'
import { ChartViewKey } from '../../../../assets/constants'
import {
    selectMultiStrikeChartConfigCallPut,
    selectMultiStrikeChartConfigChartType,
    selectMultiStrikeChartConfigCumOi,
    selectMultiStrikeChartConfigSecurities,
    selectMultiStrikeChartConfigStyles,
} from '../../../../selectors/chart'
import {
    selectMultiStrikeChartCallPut,
    selectMultiStrikeChartSecurities,
    selectMultiStrikeChartStyles,
    selectMultiStrikeChartType,
    selectMultiStrikeCumOi,
} from '../../../../selectors/multiStrikeChart'
import { getMultiStrikeCallColor, getMultiStrikePutColor } from '../../multiStrikeColorPalettes'
import { MultiStrikeSecuritiesClassType } from '../../../../reducer/multiStrikeChart.reducer'
import { AppContext } from '../../../../App'
import { Box, useTheme } from '@mui/material'
import MultiStrikeStrikeMetadataRow from './MultiStrikeStrikeMetadataRow'
import MultiStrikeStraddleMetadataRow from './MultiStrikeStraddleMetadataRow'
import MultiStrikeOiDiffMetadataRow from './MultiStrikeOiDiffMetadataRow'
import SeriesOptionMetadataDisplay from './seriesOptionMetadataDisplay'
import { resolveMultiStrikeColorPalette } from '../../multiStrikeColorPalettes'
import { MULTI_STRIKE_METADATA_TABLE_SX } from './multiStrikeMetadataLayout'

type Props = {
    viewKey: string
    multiStrikeClassType: MultiStrikeSecuritiesClassType
}

const MultiStrikeMetadataStrikeList = ({ viewKey, multiStrikeClassType }: Props) => {
    const appContext = useContext(AppContext)
    const chart = appContext.getMultiStrikeChart(viewKey)
    const theme = useTheme()
    const isWidget = useMemo(() => viewKey !== ChartViewKey.MULTI_STRIKES_CHART, [viewKey])
    const multiStrikeChartSecurities = useSelector(
        isWidget ? selectMultiStrikeChartConfigSecurities(viewKey) : selectMultiStrikeChartSecurities
    )
    const chartType = useSelector(isWidget ? selectMultiStrikeChartConfigChartType(viewKey) : selectMultiStrikeChartType)
    const cumOi = useSelector(isWidget ? selectMultiStrikeChartConfigCumOi(viewKey) : selectMultiStrikeCumOi)
    const cumCallPut = useSelector(isWidget ? selectMultiStrikeChartConfigCallPut(viewKey) : selectMultiStrikeChartCallPut)
    const chartStyles = useSelector(isWidget ? selectMultiStrikeChartConfigStyles(viewKey) : selectMultiStrikeChartStyles)
    const themeMode = theme.palette.mode === 'dark' ? 'dark' : 'light'
    const colorPalette = useMemo(
        () => resolveMultiStrikeColorPalette(chartStyles, themeMode),
        [chartStyles, themeMode]
    )

    const strikeRows = useMemo(() => {
        return Object.keys(multiStrikeClassType ?? {}).map((strike, index) => ({
            strike,
            callKey: multiStrikeClassType?.[strike]?.callSecKey,
            putKey: multiStrikeClassType?.[strike]?.putSecKey,
            callSeriesColor: getMultiStrikeCallColor(chartStyles, index, themeMode),
            putSeriesColor: getMultiStrikePutColor(chartStyles, index, themeMode),
            callPut:
                chartType === 'cumOi'
                    ? undefined
                    : multiStrikeChartSecurities?.[strike],
        }))
    }, [chartType, multiStrikeChartSecurities, multiStrikeClassType, chartStyles, themeMode])

    useEffect(() => {
        if (!chart || !multiStrikeChartSecurities || !multiStrikeClassType) return
        const callPutSecurities: MultiStrikeSecuritiesClassType = {}
        Object.keys(multiStrikeChartSecurities).forEach((strike) => {
            callPutSecurities[strike] = {
                callPut: multiStrikeChartSecurities[strike],
                callSecKey: multiStrikeClassType?.[strike]?.callSecKey,
                putSecKey: multiStrikeClassType?.[strike]?.putSecKey,
            }
        })
        if (chartType === 'cumOi') {
            chart.updateCumOiCallPutVisible()
        } else if (chartType === 'straddle') {
            chart.straddleSeries.applyVisibility()
        } else if (chartType === 'oiDiff') {
            chart.oiDiffSeries.applyVisibility()
        } else {
            chart.updateStrikeCallPutVisible(callPutSecurities)
        }
    }, [chart, multiStrikeChartSecurities, multiStrikeClassType, chartType])

    const renderTableBody = (rows: ReactNode) => (
        <Box component="table" sx={MULTI_STRIKE_METADATA_TABLE_SX} className="font-feature-num">
            <Box component="tbody">{rows}</Box>
        </Box>
    )

    if (chartType === 'cumOi') {
        return renderTableBody(
            <SeriesOptionMetadataDisplay
                strike={''}
                viewKey={viewKey}
                callSeriesColor={colorPalette.callCumulative}
                putSeriesColor={colorPalette.putCumulative}
                callKey={'cum_oi_CE'}
                putKey={'cum_oi_PE'}
                callPut={cumCallPut ?? ['CE', 'PE']}
                chartType={chartType}
            />
        )
    }

    if (chartType === 'straddle') {
        if (cumOi) {
            return renderTableBody(
                <MultiStrikeStraddleMetadataRow
                    key="straddle-cumulative"
                    viewKey={viewKey}
                    strike=""
                    seriesColor={colorPalette.callCumulative}
                    chartType={chartType}
                    aggregate
                />
            )
        }
        return renderTableBody(
            strikeRows.map((row) => (
                <MultiStrikeStraddleMetadataRow
                    key={`straddle-${row.strike}`}
                    viewKey={viewKey}
                    strike={row.strike}
                    seriesColor={row.callSeriesColor}
                    callPut={row.callPut}
                    chartType={chartType}
                />
            ))
        )
    }

    if (chartType === 'oiDiff') {
        if (cumOi) {
            return renderTableBody(
                <MultiStrikeOiDiffMetadataRow
                    key="oi-diff-cumulative"
                    viewKey={viewKey}
                    strike=""
                    seriesColor={colorPalette.callCumulative}
                    chartType={chartType}
                    aggregate
                />
            )
        }
        return renderTableBody(
            strikeRows.map((row) => (
                <MultiStrikeOiDiffMetadataRow
                    key={`oiDiff-${row.strike}`}
                    viewKey={viewKey}
                    strike={row.strike}
                    seriesColor={row.callSeriesColor}
                    callPut={row.callPut}
                    chartType={chartType}
                />
            ))
        )
    }

    return renderTableBody(
        strikeRows.map((row) => (
            <MultiStrikeStrikeMetadataRow
                key={`${row.strike}-${row.callKey ?? ''}-${row.putKey ?? ''}`}
                viewKey={viewKey}
                strike={row.strike}
                callKey={row.callKey}
                putKey={row.putKey}
                callSeriesColor={row.callSeriesColor}
                putSeriesColor={row.putSeriesColor}
                callPut={row.callPut}
                chartType={chartType}
            />
        ))
    )
}

export default memo(MultiStrikeMetadataStrikeList)
